import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import {
  X, Plus, Bug, Sparkles, Trash2, Image as ImageIcon, Video, Paperclip,
  Send, Copy, Check, Loader2, ChevronLeft, Archive,
} from "lucide-react";

const KINDS = {
  amelioration: { label: "Amélioration", icon: Sparkles, color: "#7C6FF0" },
  bug: { label: "Bug", icon: Bug, color: "#E5736B" },
};

export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list"); // list | new | compiled
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ kind: "amelioration", title: "", description: "" });
  const [pendingFiles, setPendingFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [compiled, setCompiled] = useState("");
  const [compiling, setCompiling] = useState(false);
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/feedback", { params: { status: "open" } });
      setItems(data || []);
    } catch (e) {
      console.error("feedback load failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const resetForm = () => {
    setForm({ kind: "amelioration", title: "", description: "" });
    setPendingFiles([]);
  };

  const submitNew = async () => {
    if (!form.title.trim()) {
      toast.error("Donne un petit titre à ton amélioration 🦄");
      return;
    }
    setSaving(true);
    try {
      const { data: item } = await api.post("/feedback", form);
      for (const f of pendingFiles) {
        const fd = new FormData();
        fd.append("file", f);
        try {
          await api.post(`/feedback/${item.id}/attachments`, fd);
        } catch (e) {
          toast.error(`Échec de l'envoi de ${f.name}`);
        }
      }
      toast.success("Ajouté à ta liste ✨");
      resetForm();
      setView("list");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'ajout");
    } finally {
      setSaving(false);
    }
  };

  const addFilesToItem = async (itemId, files) => {
    for (const f of files) {
      if (f.size > 50 * 1024 * 1024) {
        toast.error(`${f.name} dépasse 50 Mo`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", f);
      try {
        await api.post(`/feedback/${itemId}/attachments`, fd);
      } catch (e) {
        toast.error(`Échec de l'envoi de ${f.name}`);
      }
    }
    load();
  };

  const removeItem = async (id) => {
    try {
      await api.delete(`/feedback/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const removeAttachment = async (itemId, attId) => {
    try {
      await api.delete(`/feedback/${itemId}/attachments/${attId}`);
      load();
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const openAttachment = async (att) => {
    try {
      const { data } = await api.get(`/feedback/attachments/${att.storage_path}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Impossible d'ouvrir le média");
    }
  };

  const compile = async () => {
    setCompiling(true);
    setCopied(false);
    try {
      const { data } = await api.post("/feedback/compile", {});
      let text = data.prompt;
      const atts = data.attachments || [];
      if (atts.length) {
        text += "\n\n## 📎 Captures & vidéos jointes (liens directs)\n";
        text += atts
          .map((a) => `- ${a.media === "video" ? "Vidéo" : "Capture"} « ${a.filename} »${a.item_title ? ` (pour : ${a.item_title})` : ""} : ${API}/feedback/public/attachments/${a.path}`)
          .join("\n");
        text += "\n\n👉 Ouvre / analyse ces liens pour voir les captures liées à chaque demande.";
      }
      setCompiled(text);
      setView("compiled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rien à compiler");
    } finally {
      setCompiling(false);
    }
  };

  const copyCompiled = async () => {
    try {
      await navigator.clipboard.writeText(compiled);
      setCopied(true);
      toast.success("Copié ! Colle-le dans le chat 💬");
    } catch {
      toast.error("Copie impossible");
    }
  };

  const archiveSent = async () => {
    try {
      await api.post("/feedback/archive", {});
      toast.success("Liste archivée ✅");
      setCompiled("");
      setView("list");
      load();
    } catch {
      toast.error("Archivage impossible");
    }
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          data-testid="feedback-fab"
          onClick={() => setOpen(true)}
          aria-label="Ouvrir Pika'Licorne"
          className="fixed bottom-24 right-5 z-[60] w-16 h-16 rounded-full shadow-lg ring-2 ring-white pika-float overflow-hidden"
          style={{
            backgroundImage: "url(/mascot-pikalicorne.png)",
            backgroundSize: "250%",
            backgroundPosition: "16% 24%",
            backgroundColor: "#fff",
          }}
        >
          {items.length > 0 && (
            <span
              data-testid="feedback-fab-badge"
              className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-[#E5736B] text-white text-[11px] font-bold flex items-center justify-center ring-2 ring-white"
            >
              {items.length}
            </span>
          )}
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="feedback-panel"
          className="fixed z-[60] bg-white shadow-2xl border border-[var(--border)] flex flex-col
                     bottom-0 right-0 left-0 h-[85vh] rounded-t-2xl
                     sm:bottom-24 sm:right-5 sm:left-auto sm:w-[400px] sm:h-[560px] sm:max-h-[78vh] sm:rounded-2xl
                     pika-pop"
        >
          {/* Header */}
          <div
            className="flex items-center gap-3 p-4 rounded-t-2xl text-white"
            style={{ background: "linear-gradient(110deg,#FBC2EB 0%,#A6C1EE 50%,#C2F0E0 100%)" }}
          >
            <div
              className="w-11 h-11 rounded-full ring-2 ring-white shrink-0"
              style={{ backgroundImage: "url(/mascot-pikalicorne.png)", backgroundSize: "250%", backgroundPosition: "16% 24%", backgroundColor: "#fff" }}
              aria-label="Pika'Licorne"
            />
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-base leading-tight text-stone-800">Pika'Licorne</div>
              <div className="text-[11px] text-stone-600 leading-tight">Tes idées & bugs, compilés d'un coup</div>
            </div>
            <button data-testid="feedback-close" onClick={() => setOpen(false)} aria-label="Fermer" className="text-stone-700 hover:bg-white/40 rounded-full p-1.5">
              <X size={20} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-4">
            {view === "list" && (
              <ListView
                items={items}
                loading={loading}
                onRemove={removeItem}
                onRemoveAtt={removeAttachment}
                onOpenAtt={openAttachment}
                onAddFiles={addFilesToItem}
                onNew={() => { resetForm(); setView("new"); }}
              />
            )}
            {view === "new" && (
              <NewView
                form={form}
                setForm={setForm}
                pendingFiles={pendingFiles}
                setPendingFiles={setPendingFiles}
                fileInputRef={fileInputRef}
                onBack={() => setView("list")}
              />
            )}
            {view === "compiled" && (
              <CompiledView compiled={compiled} copied={copied} onCopy={copyCompiled} onArchive={archiveSent} onBack={() => setView("list")} />
            )}
          </div>

          {/* Footer actions */}
          {view === "list" && (
            <div className="p-3 border-t border-[var(--border)] flex gap-2">
              <button
                data-testid="feedback-new-btn"
                onClick={() => { resetForm(); setView("new"); }}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-[var(--border)] font-semibold text-sm hover:bg-stone-50"
              >
                <Plus size={16} /> Nouveau
              </button>
              <button
                data-testid="feedback-compile-btn"
                onClick={compile}
                disabled={items.length === 0 || compiling}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-white font-semibold text-sm disabled:opacity-40"
                style={{ backgroundColor: "#C26B53" }}
              >
                {compiling ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                Compiler ({items.length})
              </button>
            </div>
          )}

          {view === "new" && (
            <div className="p-3 border-t border-[var(--border)] flex gap-2">
              <button data-testid="feedback-new-back" onClick={() => setView("list")} className="px-4 py-2.5 rounded-xl border border-[var(--border)] font-semibold text-sm hover:bg-stone-50">
                <ChevronLeft size={16} />
              </button>
              <button
                data-testid="feedback-new-submit"
                onClick={submitNew}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-white font-semibold text-sm disabled:opacity-50"
                style={{ backgroundColor: "#C26B53" }}
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                Ajouter à ma liste
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function ListView({ items, loading, onRemove, onRemoveAtt, onOpenAtt, onAddFiles, onNew }) {
  if (loading) {
    return <div className="flex items-center justify-center h-full text-stone-400"><Loader2 className="animate-spin" /></div>;
  }
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-stone-400 gap-3">
        <div className="w-24 h-24 rounded-full" style={{ backgroundImage: "url(/mascot-pikalicorne.png)", backgroundSize: "215%", backgroundPosition: "16% 24%", backgroundColor: "#fff" }} aria-label="Pika'Licorne" />
        <p className="text-sm px-6">Aucune idée pour le moment.<br />Capture tes bugs et améliorations au fil de l'eau, puis compile tout d'un coup !</p>
        <button data-testid="feedback-empty-new" onClick={onNew} className="mt-1 flex items-center gap-1.5 px-4 py-2 rounded-xl text-white font-semibold text-sm" style={{ backgroundColor: "#C26B53" }}>
          <Plus size={16} /> Première idée
        </button>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="feedback-list">
      {items.map((it) => {
        const K = KINDS[it.kind] || KINDS.amelioration;
        const Icon = K.icon;
        return (
          <div key={it.id} data-testid={`feedback-item-${it.id}`} className="rounded-xl border border-[var(--border)] p-3">
            <div className="flex items-start gap-2">
              <span className="mt-0.5 shrink-0 inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: K.color }}>
                <Icon size={12} /> {K.label}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-stone-800 break-words">{it.title}</div>
                {it.description && <div className="text-xs text-stone-500 mt-0.5 whitespace-pre-wrap break-words">{it.description}</div>}
              </div>
              <button data-testid={`feedback-item-delete-${it.id}`} onClick={() => onRemove(it.id)} aria-label="Supprimer" className="text-stone-300 hover:text-[#E5736B] p-1 shrink-0">
                <Trash2 size={15} />
              </button>
            </div>
            {it.attachments?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {it.attachments.map((a) => (
                  <span key={a.id} className="group inline-flex items-center gap-1 text-[11px] bg-stone-100 rounded-lg pl-1.5 pr-1 py-1 max-w-[150px]">
                    {a.media === "video" ? <Video size={12} /> : <ImageIcon size={12} />}
                    <button onClick={() => onOpenAtt(a)} className="truncate hover:underline" data-testid={`feedback-att-open-${a.id}`}>{a.filename}</button>
                    <button onClick={() => onRemoveAtt(it.id, a.id)} aria-label="Retirer" className="opacity-0 group-hover:opacity-100 text-stone-400 hover:text-[#E5736B]"><X size={11} /></button>
                  </span>
                ))}
              </div>
            )}
            <label className="mt-2 inline-flex items-center gap-1 text-[11px] text-stone-400 hover:text-stone-600 cursor-pointer">
              <Paperclip size={12} /> Ajouter capture / vidéo
              <input type="file" accept="image/*,video/*" multiple className="hidden" data-testid={`feedback-item-addfile-${it.id}`}
                onChange={(e) => { if (e.target.files.length) onAddFiles(it.id, Array.from(e.target.files)); e.target.value = ""; }} />
            </label>
          </div>
        );
      })}
    </div>
  );
}

function NewView({ form, setForm, pendingFiles, setPendingFiles, fileInputRef, onBack }) {
  return (
    <div className="space-y-4" data-testid="feedback-new-form">
      <div className="flex gap-2">
        {Object.entries(KINDS).map(([key, K]) => {
          const Icon = K.icon;
          const active = form.kind === key;
          return (
            <button
              key={key}
              data-testid={`feedback-kind-${key}`}
              onClick={() => setForm((f) => ({ ...f, kind: key }))}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-sm font-semibold border transition-colors ${active ? "text-white border-transparent" : "text-stone-500 border-[var(--border)]"}`}
              style={active ? { backgroundColor: K.color } : {}}
            >
              <Icon size={15} /> {K.label}
            </button>
          );
        })}
      </div>
      <div>
        <label className="text-[11px] font-bold uppercase tracking-wide text-stone-400">Titre</label>
        <input
          data-testid="feedback-title-input"
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          placeholder="Ex : Le bouton X ne fonctionne pas"
          className="w-full mt-1 px-3 py-2 rounded-xl border border-[var(--border)] text-sm focus:outline-none focus:ring-2 focus:ring-[#A6C1EE]"
        />
      </div>
      <div>
        <label className="text-[11px] font-bold uppercase tracking-wide text-stone-400">Description</label>
        <textarea
          data-testid="feedback-desc-input"
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          placeholder="Décris le souhait ou le bug, ce qui se passe et ce que tu attends…"
          rows={4}
          className="w-full mt-1 px-3 py-2 rounded-xl border border-[var(--border)] text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#A6C1EE]"
        />
      </div>
      <div>
        <label className="text-[11px] font-bold uppercase tracking-wide text-stone-400">Captures / vidéos</label>
        <button
          type="button"
          data-testid="feedback-pick-files"
          onClick={() => fileInputRef.current?.click()}
          className="w-full mt-1 flex items-center justify-center gap-1.5 py-3 rounded-xl border border-dashed border-[var(--border)] text-sm text-stone-500 hover:bg-stone-50"
        >
          <Paperclip size={15} /> Joindre image ou vidéo
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          className="hidden"
          onChange={(e) => {
            const files = Array.from(e.target.files).filter((f) => {
              if (f.size > 50 * 1024 * 1024) { toast.error(`${f.name} dépasse 50 Mo`); return false; }
              return true;
            });
            setPendingFiles((p) => [...p, ...files]);
            e.target.value = "";
          }}
        />
        {pendingFiles.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {pendingFiles.map((f, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-[11px] bg-stone-100 rounded-lg px-2 py-1 max-w-[150px]">
                {f.type.startsWith("video") ? <Video size={12} /> : <ImageIcon size={12} />}
                <span className="truncate">{f.name}</span>
                <button onClick={() => setPendingFiles((p) => p.filter((_, j) => j !== i))} className="text-stone-400 hover:text-[#E5736B]"><X size={11} /></button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CompiledView({ compiled, copied, onCopy, onArchive, onBack }) {
  return (
    <div className="flex flex-col h-full" data-testid="feedback-compiled">
      <button onClick={onBack} className="self-start flex items-center gap-1 text-xs text-stone-400 hover:text-stone-600 mb-2">
        <ChevronLeft size={14} /> Retour
      </button>
      <p className="text-xs text-stone-500 mb-2">Copie ce brief et colle-le dans le chat pour que tout soit traité d'un coup 💬</p>
      <textarea
        data-testid="feedback-compiled-text"
        readOnly
        value={compiled}
        className="flex-1 w-full p-3 rounded-xl border border-[var(--border)] text-xs font-mono resize-none focus:outline-none"
      />
      <div className="flex gap-2 mt-3">
        <button data-testid="feedback-copy-btn" onClick={onCopy} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-white font-semibold text-sm" style={{ backgroundColor: copied ? "#3FB984" : "#C26B53" }}>
          {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? "Copié" : "Copier le brief"}
        </button>
        <button data-testid="feedback-archive-btn" onClick={onArchive} className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border border-[var(--border)] font-semibold text-sm text-stone-500 hover:bg-stone-50">
          <Archive size={16} /> Archiver
        </button>
      </div>
    </div>
  );
}
